import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../../../app/store';
import { selectActiveRestaurantCode } from '../../../selectors/restaurant';
import {
  InfoSlashCommandOptions,
  resolveInfoSlashCommandExclude,
} from '../../../utils/infoSlashCommands.options';

const selectConfig = (state: RootState) => state.config;
const selectNodeEnv = createSelector(selectConfig, (state) => state.NODE_ENV);
const selectFeatureFlagTaskRouter = createSelector(
  selectConfig,
  (state) => state.FEATUREFLAG_TASKROUTER
);
const selectTaskRouterWaitingTime = createSelector(
  selectConfig,
  (state) => state.TASKROUTER_WAITING_TIME
);
const selectFeatureFlagMenuVersion = createSelector(
  selectConfig,
  (state) => state.FEATURE_FLAG_MENU_VERSION
);
const selectTaskRouterNotificationSound = createSelector(
  selectConfig,
  (state) => state.TASKROUTER_NOTIFICATION_SOUND
);
const selectPagerdutyIntegrationKey = createSelector(
  selectConfig,
  (state) => state.PAGERDUTY_INTEGRATION_KEY
);
const selectSessionBufferAPI = createSelector(
  selectConfig,
  (state) => state.SESSION_BUFFER_API
);
const selectSessionBufferAPIAuthentication = createSelector(
  selectConfig,
  (state) => state.SESSION_BUFFER_API_AUTHENTICATION
);
const selectAuthURL = createSelector(selectConfig, (state) => state.AUTH_URL);
const selectTRAgentAutoAssignment = createSelector(
  selectConfig,
  (state) => state.TASK_ROUTER_AGENT_AUTO_ASSIGNMENT
);
const selectDisableIntentInterval = createSelector(
  selectConfig,
  (state) => state.DISABLE_INTENT_INTERVAL
);

const selectEventAPI = createSelector(selectConfig, (state) => state.EVENT_API);

const selectEventAPIAuth = createSelector(
  selectConfig,
  (state) => state.EVENT_API_BASIC_AUTH_PARAM
);

const selectFeatureFlagAudioWebsocket = createSelector(
  selectConfig,
  (state) => state.FEATURE_FLAG_AUDIO_BACKEND
);

const selectFinishOrderTimer = createSelector(
  selectConfig,
  (state) => state.FINISH_ORDER_TIMER
);

const selectAgentInactivityTimer = createSelector(
  selectConfig,
  (state) => state.AGENT_INACTIVITY_TIMER
);

const selectFeatureFlagAutoCombo = createSelector(
  selectConfig,
  (state) => state.FEATURE_FLAG_AUTO_COMBO
);

const selectFeatureFlagBMAutoCombo = createSelector(
  selectConfig,
  (state) => state.FEATURE_FLAG_BM_AUTO_COMBO
);

const selectFeatureFlagLoyaltySupport = createSelector(
  selectConfig,
  (state) => state.FEATURE_FLAG_LOYALTY_SUPPORT
);

const selectAIEscalationUpsell = createSelector(
  selectConfig,
  (state) => state.AI_ESCALATION_UPSELL
);

const selectIdleLogOutTime = createSelector(
  selectConfig,
  (state) => state.IDLE_LOGOUT_TIME
);

const selectFeatureFlagFinalOrderValidation = createSelector(
  selectConfig,
  (state) => state.FEATURE_FLAG_FINAL_ORDER_VALIDATION
);

const selectFeatureFlagPosCart = createSelector(
  selectConfig,
  (state) => state.FEATURE_FLAG_POS_CART
);

const selectFeatureFlagItemConversion = createSelector(
  selectConfig,
  (state) => state.FEATURE_FLAG_ITEM_CONVERSION
);

const selectFeatureFlagOCBReadback = createSelector(
  selectConfig,
  (state) => state.FEATURE_FLAG_OCB_READBACK
);

const selectFinalOrderPendingTimeout = createSelector(
  selectConfig,
  (state) => state.FINAL_ORDER_PENDING_TIMEOUT
);

const selectFinalOrderCompletedTimeout = createSelector(
  selectConfig,
  (state) => state.FINAL_ORDER_COMPLETED_TIMEOUT
);

const selectFeatureFlagNotifyHitlBuild = createSelector(
  selectConfig,
  (state) => state.FEATURE_FLAG_NOTIFY_HITL_BUILD
);

const selectFinishOrderButtonDisableInterval = createSelector(
  selectConfig,
  (state) => state.FINISH_ORDER_BUTTON_DISABLE_INTERVAL
);

const selectFeatureFlagSessionEndOrderActionChanges = createSelector(
  selectConfig,
  (state) => state.FEATURE_FLAG_SESSION_END_ORDER_ACTION_CHANGES
);

const selectFeatureFlagTimerResetOnCartUpdate = createSelector(
  selectConfig,
  (state) => state.FEATURE_FLAG_TIMER_RESET_ON_CART_UPDATE
);

const selectFeatureFlagAgentCart = createSelector(
  selectConfig,
  (state) => state.FEATURE_FLAG_AGENT_CART
);

const selectFeatureFlagSuppressUpsell = createSelector(
  selectConfig,
  (state) => state.FEATURE_FLAG_SUPPRESS_UPSELL
);

const selectFeatureFlagDoNotUpsellInExpressMode = createSelector(
  selectConfig,
  (state) => state.FEATURE_FLAG_DO_NOT_UPSELL_IN_EXPRESS_MODE
);

const selectFeatureFlagUnresolvedItems = createSelector(
  selectConfig,
  (state) => state.FEATURE_FLAG_UNRESOLVED_ITEMS
);

const selectFeatureFlagHitl3 = createSelector(
  selectConfig,
  (state) => state.FEATURE_FLAG_HITL_3_0
);

const selectFeatureFlagHitlExpressTimer = createSelector(
  selectConfig,
  (state) => state.FEATURE_FLAG_HITL_EXPRESS_TIMER
);

const selectHitlExpressTimerMaxSeconds = createSelector(
  selectConfig,
  (state) => state.HITL_EXPRESS_TIMER_MAX_SECONDS
);

const selectAgentResponseTimeLimitSeconds = createSelector(
  selectConfig,
  (state) => state.AGENT_RESPONSE_TIME_LIMIT_SECONDS
);

const selectFeatureFlagMenuModuleErrorUi = createSelector(
  selectConfig,
  (state) => state.FEATURE_FLAG_MENU_MODULE_ERROR_UI
);

/** HITL-1747 kill switch, read at every buildFullMenuUnifiedItem call site. */
export const selectIsMenuItemBuildCacheEnabled = createSelector(
  selectConfig,
  (state) => state.FEATURE_FLAG_MENU_ITEM_BUILD_CACHE
);

/** HITL-1748 kill switch, read at every buildFullMenuUnifiedItem call site. */
export const selectIsLazyOptionBuildEnabled = createSelector(
  selectConfig,
  (state) => state.FEATURE_FLAG_LAZY_OPTION_BUILD
);

/**
 * HITL-1749: the `/info_*` slash-command options for the active store, or null
 * while `info_slash_commands` is off. Read wherever the top-level item
 * list is built (menu load, daypart rollover).
 */
// Memoized on the two fields, not the whole config: a new options object
// restarts the rollover intervals that depend on it.
const selectFeatureFlagInfoSlashCommands = createSelector(
  selectConfig,
  (state) => state.FEATURE_FLAG_INFO_SLASH_COMMANDS
);
const selectInfoSlashCommandsPayload = createSelector(
  selectConfig,
  (state) => state.INFO_SLASH_COMMANDS_PAYLOAD
);
export const selectInfoSlashCommandOptions = createSelector(
  selectFeatureFlagInfoSlashCommands,
  selectInfoSlashCommandsPayload,
  selectActiveRestaurantCode,
  (isEnabled, payload, restaurantCode): InfoSlashCommandOptions =>
    isEnabled
      ? { exclude: resolveInfoSlashCommandExclude(payload, restaurantCode) }
      : null
);

/** HITL-1761: the AI's slot_bound_upsell_enabled, read where an agent asks a slot. */
export const selectIsSlotBoundUpsellEnabled = createSelector(
  selectConfig,
  (state) => state.FEATURE_FLAG_SLOT_BOUND_UPSELL
);

/** HITL-1699 kill switch, read where the red alert builds its customer line. */
const selectFeatureFlagMenuModuleErrorNamedTts = createSelector(
  selectConfig,
  (state) => state.FEATURE_FLAG_MENU_MODULE_ERROR_NAMED_TTS
);

/** HITL-1551 kill switch, read at the staff-intervention call site so that with
 * the flag off the intervention takes literally today's path. */
const selectFeatureFlagAutofillFinalOrderOnIntervention = createSelector(
  selectConfig,
  (state) => state.FEATURE_FLAG_AUTOFILL_FINAL_ORDER_ON_INTERVENTION
);

/** HITL-1562 kill switch for the auto-submit autofill (post-car_exit timer
 * expiry), read at the call site so that with the flag off it takes literally
 * today's path. Independent of the intervention flag above. */
const selectFeatureFlagAutofillFinalOrderOnAutoSubmit = createSelector(
  selectConfig,
  (state) => state.FEATURE_FLAG_AUTOFILL_FINAL_ORDER_ON_AUTO_SUBMIT
);

const selectFeatureFlagOrderCheckRetry = createSelector(
  selectConfig,
  (state) => state.FEATURE_FLAG_ORDER_CHECK_RETRY
);

const selectOrderCheckTimeoutSeconds = createSelector(
  selectConfig,
  (state) => state.ORDER_CHECK_TIMEOUT_SECONDS
);

const selectFeatureFlagOrderStatusBanner = createSelector(
  selectConfig,
  (state) => state.FEATURE_FLAG_ORDER_STATUS_BANNER
);

const selectFeatureFlagSecondaryAsrEnabled = createSelector(
  selectConfig,
  (state) => state.FEATURE_FLAG_SECONDARY_ASR_ENABLED
);

const selectConsolidatedMenuBrands = createSelector(
  selectConfig,
  (state) => state.CONSOLIDATED_MENU_BRANDS
);

const selectConsolidatedMenuExcludedRestaurants = createSelector(
  selectConfig,
  (state) => state.CONSOLIDATED_MENU_EXCLUDED_RESTAURANTS
);

const selectFeatureFlagIssueReporting = createSelector(
  selectConfig,
  (state) => state.FEATURE_FLAG_ISSUE_REPORTING
);

/**
 * HITL-1492 Pillar 3: the gate every issue-reporting consumer reads.
 *
 * The AND lives here, once, so the four UI tickets that follow cannot get it
 * wrong: the bottom action bar the Issue button lives in only exists in
 * MainPageLayout (hitl_3_0), but the feature stays independently killable.
 */
const selectIsIssueReportingEnabled = createSelector(
  selectFeatureFlagHitl3,
  selectFeatureFlagIssueReporting,
  (isHitl3, isIssueReporting) => Boolean(isHitl3 && isIssueReporting)
);

const selectFeatureFlagAiSuggestionAutoAdd = createSelector(
  selectConfig,
  (state) => state.FEATURE_FLAG_AI_SUGGESTION_AUTO_ADD
);

/**
 * HITL-1429: the gate the AI-suggestion accept path reads before it touches the
 * cart.
 *
 * The AND lives here, once: `hitl_3_0` because the suggestion card only exists
 * in the HITL 3.0 Pulse pane, `agent_cart` because the feature mutates the
 * agent cart — both are already preconditions for the card even being on
 * screen, so this selector never enables anything the agent cannot see. The
 * feature stays independently killable.
 */
const selectIsAISuggestionAutoAddEnabled = createSelector(
  selectFeatureFlagHitl3,
  selectFeatureFlagAgentCart,
  selectFeatureFlagAiSuggestionAutoAdd,
  (isHitl3, isAgentCart, isAutoAdd) =>
    Boolean(isHitl3 && isAgentCart && isAutoAdd)
);

// Deliberately NOT exported. HITL 7.0 is a set of deltas on top of the 3.0
// console, not a UX of its own, so reading hitl_7_0 on its own would gate a
// change onto a legacy layout that has nowhere to put it. The only way in is
// selectIsHitl7Enabled below.
const selectFeatureFlagHitl7 = createSelector(
  selectConfig,
  (state) => state.FEATURE_FLAG_HITL_7_0
);

/**
 * REL-8161: the gate every HITL 7.0 consumer reads.
 *
 * The AND lives here, once, so the ten UI tickets that follow cannot get it
 * wrong. `hitl_3_0` because every 7.0 change moves, restyles or removes
 * something that only exists in the 3.0 console — the status bar it widens,
 * the Copilot card it strips, the bubbles it flattens. With 3.0 off there is
 * nothing for those deltas to act on, so 7.0 must read as off too.
 *
 * The set stays independently killable: turning hitl_7_0 off returns the
 * console to 3.0 exactly, without touching HITL 3.0 itself.
 */
const selectIsHitl7Enabled = createSelector(
  selectFeatureFlagHitl3,
  selectFeatureFlagHitl7,
  (isHitl3, isHitl7) => Boolean(isHitl3 && isHitl7)
);

/**
 * REL-8155 / 7.2.0: the gate every issue-reporting surface added on the 7.0
 * console reads — the item picker, the staff-intervention consolidation and the
 * three flag gestures, plus whatever later tickets hang off the agent cart.
 *
 * Composed from `selectIsIssueReportingEnabled` rather than re-ANDing the two
 * flags by hand, so the `hitl_3_0 AND hitl_issue_reporting` rule stays stated
 * exactly once. All three terms are load-bearing:
 *
 * - `hitl_3_0` + `hitl_issue_reporting` - there is no issue report to attach an
 *   item to, and no flag panel to open, without the feature underneath.
 * - `hitl_7_0` - every one of these surfaces is a mark placed on 7.0 furniture.
 *   The suggestion flag sits in the inline transcript box that only exists
 *   under 7.0; the cart-row flag sits in a row the 7.0 centre pane re-laid out.
 *   With 7.0 off those marks would be hung on a layout that was never measured
 *   for them.
 *
 * This deliberately does NOT widen `selectIsIssueReportingEnabled`. That gate is
 * live in production everywhere except Fazolis and keeps answering the old
 * question — may this console report issues at all. Turning `hitl_7_0` off
 * returns issue reporting to exactly its 6.x behaviour: the Issue button, the
 * transcript gestures and the backlog all keep working, and only the new
 * surfaces disappear.
 */
const selectIsIssueReportingHitl7Enabled = createSelector(
  selectIsIssueReportingEnabled,
  selectFeatureFlagHitl7,
  (isIssueReporting, isHitl7) => Boolean(isIssueReporting && isHitl7)
);

// Deliberately NOT exported, for the same reason as selectFeatureFlagHitl7
// above: the centre pane is a delta on the 7.0 console, so reading this flag
// on its own would gate the new rows onto a layout with nowhere to put them.
// The only way in is selectIsHitl7CenterPaneEnabled below.
const selectFeatureFlagHitl7CenterPane = createSelector(
  selectConfig,
  (state) => state.FEATURE_FLAG_HITL_7_CENTER_PANE
);

/**
 * HITL-1603: the gate every centre-pane consumer reads.
 *
 * The AND lives here, once, so no later ticket can get it wrong. All three
 * terms are load-bearing:
 *
 * - `hitl_3_0` - the pane is rendered from MainPageLayout, which is the 3.0
 *   console. With 3.0 off there is no centre pane to rework at all.
 * - `hitl_7_0` - the rows are laid out against the 7.0 centre pane: the
 *   OmniBar pinned at its foot and the status bar moved out above it. With
 *   7.0 off the pane still has the 3.0 geometry the rows were never measured
 *   against.
 * - `hitl_7_center_pane` - so the rows stay killable on their own, without
 *   taking HITL 3.0 or the rest of the 7.0 refinements down with them.
 *
 * Turning hitl_7_center_pane off returns the centre pane to the 7.0 console
 * exactly, chip grid and all.
 */
const selectIsHitl7CenterPaneEnabled = createSelector(
  selectFeatureFlagHitl3,
  selectFeatureFlagHitl7,
  selectFeatureFlagHitl7CenterPane,
  (isHitl3, isHitl7, isCenterPane) =>
    Boolean(isHitl3 && isHitl7 && isCenterPane)
);

const selectFeatureFlagShadowMode = createSelector(
  selectConfig,
  (state) => state.FEATURE_FLAG_SHADOW_MODE
);

/**
 * HITL-1604: the one gate every supervised-mode consumer reads.
 *
 * The AND lives here, once, so no later ticket can get it wrong. All three
 * terms are load-bearing:
 *
 * - `hitl_3_0` - every surface the mode touches is rendered from
 *   MainPageLayout, which only exists under 3.0.
 * - `hitl_7_0` - the mode switch sits in the 7.0 header beside the restaurant
 *   picker, and takes its typography from it. With 7.0 off there is no header
 *   of that shape to put it in.
 * - `hitl_shadow_mode` - so supervised mode stays killable on its own.
 *
 * Deliberately does NOT include `agent_cart`: that flag gates publishing the
 * agent cart to the AI (useAgentCartSync), which is outbound and therefore a
 * concern of Intercept alone, not of whether the mode exists.
 *
 * Turning hitl_shadow_mode off returns the console to 7.x exactly - no mode
 * switch, and every agent action live as it is today.
 */
const selectIsShadowModeEnabled = createSelector(
  selectFeatureFlagHitl3,
  selectFeatureFlagHitl7,
  selectFeatureFlagShadowMode,
  (isHitl3, isHitl7, isShadowMode) =>
    Boolean(isHitl3 && isHitl7 && isShadowMode)
);

// Deliberately not exported: the panel replaces the 7.x centre-pane rows, so
// this flag on its own would gate it onto a pane with no rows to replace.
const selectFeatureFlagRepeatedSlotPanel = createSelector(
  selectConfig,
  (state) => state.FEATURE_FLAG_REPEATED_SLOT_PANEL
);

/**
 * The gate every repeated-slot-panel consumer reads: the centre-pane gate
 * (`hitl_3_0 AND hitl_7_0 AND hitl_7_center_pane`) AND
 * `hitl_repeated_slot_panel`. Turning the last one off returns the pane to the
 * 7.x rows exactly.
 */
const selectIsRepeatedSlotPanelEnabled = createSelector(
  selectIsHitl7CenterPaneEnabled,
  selectFeatureFlagRepeatedSlotPanel,
  (isCenterPane, isRepeatedSlotPanel) =>
    Boolean(isCenterPane && isRepeatedSlotPanel)
);

// Deliberately not exported, for the same reason as the repeated-slot panel's:
// the stepper sits on the 7.x option rows.
const selectFeatureFlagModifierQuantity = createSelector(
  selectConfig,
  (state) => state.FEATURE_FLAG_MODIFIER_QUANTITY
);

/**
 * HITL-1756: the gate every modifier-count consumer reads — the centre-pane
 * gate (`hitl_3_0 AND hitl_7_0 AND hitl_7_center_pane`) AND
 * `modifier_count_enabled`. Turning the last one off returns the pane and the
 * cart to one-of-each exactly.
 */
const selectIsModifierQuantityEnabled = createSelector(
  selectIsHitl7CenterPaneEnabled,
  selectFeatureFlagModifierQuantity,
  (isCenterPane, isModifierQuantity) =>
    Boolean(isCenterPane && isModifierQuantity)
);

export {
  selectAgentInactivityTimer,
  selectAuthURL,
  selectConfig,
  selectDisableIntentInterval,
  selectEventAPI,
  selectEventAPIAuth,
  selectFeatureFlagAutoCombo,
  selectFeatureFlagBMAutoCombo,
  selectFeatureFlagLoyaltySupport,
  selectFeatureFlagAudioWebsocket,
  selectFeatureFlagMenuVersion,
  selectFeatureFlagPosCart,
  selectFeatureFlagItemConversion,
  selectFeatureFlagOCBReadback,
  selectFeatureFlagTaskRouter,
  selectFinishOrderTimer,
  selectNodeEnv,
  selectPagerdutyIntegrationKey,
  selectSessionBufferAPI,
  selectSessionBufferAPIAuthentication,
  selectTRAgentAutoAssignment,
  selectTaskRouterNotificationSound,
  selectTaskRouterWaitingTime,
  selectAIEscalationUpsell,
  selectIdleLogOutTime,
  selectFeatureFlagFinalOrderValidation,
  selectFinalOrderPendingTimeout,
  selectFinalOrderCompletedTimeout,
  selectFeatureFlagNotifyHitlBuild,
  selectFinishOrderButtonDisableInterval,
  selectFeatureFlagSessionEndOrderActionChanges,
  selectFeatureFlagTimerResetOnCartUpdate,
  selectFeatureFlagAgentCart,
  selectFeatureFlagSuppressUpsell,
  selectFeatureFlagDoNotUpsellInExpressMode,
  selectFeatureFlagUnresolvedItems,
  selectFeatureFlagHitl3,
  selectFeatureFlagHitlExpressTimer,
  selectHitlExpressTimerMaxSeconds,
  selectAgentResponseTimeLimitSeconds,
  selectFeatureFlagMenuModuleErrorUi,
  selectFeatureFlagMenuModuleErrorNamedTts,
  selectFeatureFlagOrderCheckRetry,
  selectOrderCheckTimeoutSeconds,
  selectFeatureFlagOrderStatusBanner,
  selectFeatureFlagSecondaryAsrEnabled,
  selectConsolidatedMenuBrands,
  selectConsolidatedMenuExcludedRestaurants,
  selectFeatureFlagIssueReporting,
  selectIsIssueReportingEnabled,
  selectFeatureFlagAutofillFinalOrderOnIntervention,
  selectFeatureFlagAutofillFinalOrderOnAutoSubmit,
  selectFeatureFlagAiSuggestionAutoAdd,
  selectIsAISuggestionAutoAddEnabled,
  selectIsHitl7Enabled,
  selectIsHitl7CenterPaneEnabled,
  selectFeatureFlagShadowMode,
  selectIsShadowModeEnabled,
  selectIsRepeatedSlotPanelEnabled,
  selectIsModifierQuantityEnabled,
  selectIsIssueReportingHitl7Enabled,
};
